import {
  PublicStateSchema,
  SnapshotSchema,
  clockOffsetFromDateHeader,
  type PublicState,
  type Snapshot,
} from '@jobfair/shared';
import { KEYS, local } from './storage';

export interface FeedSnapshot {
  state: PublicState | null;
  snapshot: Snapshot | null;
  /** Server minus device clock, in ms. 0 until a Date header has been read. */
  offset: number;
  /** Epoch ms (device clock) of the last successful state fetch, or 0. */
  lastOkAt: number;
  /** True when what we hold came from this browser's cache and nothing newer has arrived yet. */
  fromCache: boolean;
  /** Consecutive failed polls. 0 when healthy. */
  failures: number;
}

export interface FeedOptions {
  stateUrl: string;
  fetchImpl?: typeof fetch;
  /** Device clock. Injected for tests. */
  now?: () => number;
  /** Poll period while the tab is visible. */
  visibleMs?: number;
  /** Poll period while the tab is hidden. */
  hiddenMs?: number;
  /** Upper bound for backoff after failures. */
  maxBackoffMs?: number;
}

const OFFSET_SAMPLES = 5;
const MAX_RTT_MS = 5000;

/**
 * Keeps the page in sync with the published fair: polls the tiny state.json, and refetches the
 * (immutable) data snapshot whenever state.dataVersion changes. Nothing here ever hits a database.
 *
 * - No cache-busting query string: the CDN serves state.json with a short TTL and that is the point.
 * - A state with a lower `rev` than the one held is ignored, so a stale CDN node can't roll the page back.
 * - The last good state and snapshot are kept in localStorage, so a reload during an outage still renders.
 * - Failures back off exponentially with jitter, and the last good data stays on screen.
 */
export class FeedClient {
  private snap: FeedSnapshot = {
    state: null,
    snapshot: null,
    offset: 0,
    lastOkAt: 0,
    fromCache: false,
    failures: 0,
  };
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private running = false;
  private failures = 0;
  private samples: { rtt: number; offset: number }[] = [];
  private inFlight = false;
  private readonly opt: Required<Omit<FeedOptions, 'stateUrl'>> & { stateUrl: string };

  constructor(options: FeedOptions) {
    this.opt = {
      fetchImpl: (...a) => fetch(...a),
      now: () => Date.now(),
      visibleMs: 10_000,
      hiddenMs: 60_000,
      maxBackoffMs: 60_000,
      ...options,
    };
    this.loadCache();
  }

  getSnapshot = (): FeedSnapshot => this.snap;

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  start(): void {
    if (this.running) return;
    this.running = true;
    if (typeof document !== 'undefined')
      document.addEventListener('visibilitychange', this.onVisibility);
    void this.tick();
  }

  stop(): void {
    this.running = false;
    clearTimeout(this.timer);
    if (typeof document !== 'undefined')
      document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** Poll right now (used when the tab becomes visible again). */
  refresh(): void {
    if (!this.running) return;
    clearTimeout(this.timer);
    void this.tick();
  }

  private onVisibility = () => {
    if (document.visibilityState === 'visible') this.refresh();
  };

  private emit(patch: Partial<FeedSnapshot>) {
    this.snap = { ...this.snap, ...patch };
    this.listeners.forEach((l) => l());
  }

  private loadCache() {
    const st = PublicStateSchema.safeParse(local.json<unknown>(KEYS.feedState, null));
    const sn = SnapshotSchema.safeParse(local.json<unknown>(KEYS.feedSnapshot, null));
    // A state is only usable with the snapshot it points to.
    if (st.success && sn.success && st.data.dataVersion === sn.data.version) {
      this.snap = { ...this.snap, state: st.data, snapshot: sn.data, fromCache: true };
    }
  }

  /** The state is tiny and saved on every change; the snapshot is large and saved only when it changes. */
  private saveState(state: PublicState) {
    local.set(KEYS.feedState, JSON.stringify(state));
  }

  private saveSnapshot(snapshot: Snapshot) {
    local.set(KEYS.feedSnapshot, JSON.stringify(snapshot));
  }

  private schedule(ms: number) {
    if (!this.running) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.tick(), ms);
  }

  private nextDelay(): number {
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const base = hidden ? this.opt.hiddenMs : this.opt.visibleMs;
    const backoff = this.failures
      ? Math.min(this.opt.maxBackoffMs, base * 2 ** (this.failures - 1))
      : base;
    // +-20% jitter so a thousand clients never line up on the same second.
    return Math.round(backoff * (0.8 + Math.random() * 0.4));
  }

  private async tick(): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    try {
      await this.pollState();
      await this.syncSnapshot();
      this.failures = 0;
      if (this.snap.failures) this.emit({ failures: 0 });
    } catch {
      this.failures++;
      this.emit({ failures: this.failures });
    } finally {
      this.inFlight = false;
      this.schedule(this.nextDelay());
    }
  }

  private async pollState(): Promise<void> {
    const t0 = this.opt.now();
    const res = await this.opt.fetchImpl(this.opt.stateUrl, { cache: 'no-store' });
    const t1 = this.opt.now();
    if (!res.ok) throw new Error(`state ${res.status}`);
    const parsed = PublicStateSchema.safeParse(await res.json());
    if (!parsed.success) throw new Error('state invalid');

    this.recordOffset(res.headers.get('date'), t0, t1);

    const held = this.snap.state;
    if (held && parsed.data.rev < held.rev) {
      // Stale CDN node. Not an error, but don't go backwards.
      this.emit({ lastOkAt: t1 });
      return;
    }
    this.emit({ state: parsed.data, lastOkAt: t1 });
    this.saveState(parsed.data);
  }

  private recordOffset(dateHeader: string | null, t0: number, t1: number) {
    const off = clockOffsetFromDateHeader(dateHeader, t0, t1);
    if (off == null || t1 - t0 > MAX_RTT_MS) return;
    this.samples.push({ rtt: t1 - t0, offset: off });
    if (this.samples.length > OFFSET_SAMPLES) this.samples.shift();
    // The sample with the shortest round trip has the least latency error.
    const best = this.samples.reduce((a, b) => (b.rtt < a.rtt ? b : a));
    if (best.offset !== this.snap.offset) this.emit({ offset: best.offset });
  }

  private async syncSnapshot(): Promise<void> {
    const { state, snapshot } = this.snap;
    if (!state || snapshot?.version === state.dataVersion) {
      if (snapshot && this.snap.fromCache) this.emit({ fromCache: false });
      return;
    }
    const res = await this.opt.fetchImpl(state.dataUrl);
    if (!res.ok) throw new Error(`data ${res.status}`);
    const parsed = SnapshotSchema.safeParse(await res.json());
    if (!parsed.success) throw new Error('data invalid');
    if (parsed.data.version !== state.dataVersion) throw new Error('data version mismatch');
    this.emit({ snapshot: parsed.data, fromCache: false });
    this.saveSnapshot(parsed.data);
  }
}
